<template>
  <main class="biblioteca">
    <section class="biblioteca__hero">
      <div class="biblioteca__container biblioteca__hero-grid">
        <div class="biblioteca__hero-copy">
          <DsText as="bebas2" tag="h1" class="biblioteca__hero-title">
            <span class="biblioteca__hero-title-line">
              <span class="biblioteca__hero-title-accent">Tu camino</span>
              <span class="biblioteca__hero-title-base">con HIR Casa</span>
            </span>
          </DsText>
          <DsText as="subtitle7" class="biblioteca__hero-subtitle">
            Encuentra la información que necesitas en un solo lugar.
          </DsText>
          <div class="biblioteca__hero-body">
            <DsText as="body3" class="biblioteca__hero-paragraph">
              Aquí encontrarás guías, documentos y recursos que te acompañarán en cada etapa de tu proceso con HIR Casa.
              <br />
              ¿No encuentras lo que buscas? Contáctanos por Chat o WhatsApp.
              <br class="biblioteca__hero-desktop-break" />
              Con gusto te ayudaremos.
            </DsText>
          </div>
        </div>
        <picture class="biblioteca__hero-media">
          <source :srcset="heroMobile" media="(max-width: 599px)" />
          <source :srcset="heroTablet" media="(max-width: 1024px)" />
          <img
            :src="heroDesktop"
            alt="Familias y clientes celebrando su camino con HIR Casa"
            class="biblioteca__hero-image"
          />
        </picture>
      </div>
    </section>
    <section class="biblioteca__search">
      <div class="biblioteca__container biblioteca__search-wrap">
        <div class="biblioteca__search-box">
          <span class="biblioteca__search-icon" aria-hidden="true">
            <img :src="searchIcon" alt="" class="biblioteca__search-icon-image" />
          </span>

          <input
            v-model.trim="searchQuery"
            ref="searchInput"
            aria-label="Busca un artículo de Material de apoyo"
            aria-controls="support-search-results"
            :aria-activedescendant="filteredSearchEntries.length ? `support-result-${activeResult}` : undefined"
            @keydown.down.prevent="moveResult(1)"
            @keydown.up.prevent="moveResult(-1)"
            @keydown.esc="searchQuery = ''"
            type="search"
            role="combobox"
            aria-autocomplete="list"
            :aria-expanded="Boolean(searchQuery)"
            class="biblioteca__search-input"
            placeholder="Busca un artículo"
            @keydown.enter.prevent="openFirstSearchResult"
          />

          <div
            v-show="searchQuery"
            ref="resultsPanel"
            :class="{ 'biblioteca__search-results--above': placement === 'above' }"
            :style="{ maxHeight: `${maxHeight}px` }"
            id="support-search-results"
            role="listbox"
            aria-label="Artículos encontrados"
            class="biblioteca__search-results"
            aria-live="polite"
          >
            <p v-if="!filteredSearchEntries.length" class="biblioteca__empty">
              No encontramos artículos. Prueba con otra palabra.
            </p>
            <button
              v-for="(entry, index) in filteredSearchEntries"
              :key="`${entry.title}-${entry.routeName}`"
              :id="`support-result-${index}`"
              role="option"
              :aria-selected="activeResult === index"
              :class="{ 'biblioteca__search-result--active': activeResult === index }"
              type="button"
              class="biblioteca__search-result"
              @click="goToArticle(entry)"
            >
              <span class="biblioteca__search-result-label">{{ entry.title }}</span>
              <span class="biblioteca__search-result-category">{{ entry.category }}</span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <section class="biblioteca__library">
      <div class="biblioteca__container">
        <div class="biblioteca__section-copy">
          <DsText as="bebas4" tag="h2" class="biblioteca__section-title">Encuentra lo que necesitas</DsText>
          <DsText as="body3" class="biblioteca__section-description">
            Explora cada sección y encuentra la información que preparamos especialmente para ti.
          </DsText>
        </div>

        <div class="biblioteca__grid">
          <BibliotecaTopicCard
            v-for="card in topicCards"
            :key="card.id"
            class="biblioteca__topic-card"
            :id="`support-topic-${card.id}`"
            :icon="card.icon"
            :title="card.title"
            :description="card.description"
            :mobile-description="card.mobileDescription"
            :mobile-description-breakpoint="card.mobileDescriptionBreakpoint"
            :items="card.items.filter(item => item.showInCard !== false)"
            :is-open="activeTopicCardId === card.id"
            @toggle-card="toggleTopicCard(card.id)"
            @navigate="goToArticle"
          />
        </div>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import DsText from '../atoms/DsText.vue'
import { useListboxViewport } from '../composables/useListboxViewport'
import BibliotecaTopicCard from './BibliotecaTopicCard.vue'
import heroDesktop from '../../../assets/Biblioteca/biblioteca_hero_desktop.png'
import heroTablet from '../../../assets/Biblioteca/biblioteca_hero_tablet.webp'
import heroMobile from '../../../assets/Biblioteca/biblioteca_hero_mobile.png'
import searchIcon from '../../../assets/Biblioteca/biblioteca_search.svg'
import { supportTopics, searchSupportArticles, type SupportArticle } from '../data/supportCatalog'

const router = useRouter()
const searchQuery = ref('')
const activeTopicCardId = ref<string | null>(null)
const activeResult = ref(0)
const searchInput = ref<HTMLInputElement | null>(null)
const resultsPanel = ref<HTMLElement | null>(null)
const { placement, maxHeight } = useListboxViewport(searchInput, resultsPanel, searchQuery, activeResult)
const topicCards = supportTopics
const filteredSearchEntries = computed(() => searchSupportArticles(searchQuery.value))
watch(searchQuery, () => {
  activeResult.value = 0
})

function goToArticle(article: SupportArticle) {
  if (!article.routeName) return
  searchQuery.value = ''
  router.push({ name: article.routeName, hash: article.hash })
}
function openFirstSearchResult() {
  const article = filteredSearchEntries.value[activeResult.value]
  if (article) goToArticle(article)
}
function moveResult(offset: number) {
  const length = filteredSearchEntries.value.length
  if (length) activeResult.value = (activeResult.value + offset + length) % length
}
function toggleTopicCard(cardId: string) {
  activeTopicCardId.value = activeTopicCardId.value === cardId ? null : cardId
}
</script>

<style scoped>
.biblioteca {
  background: var(--ds-page-bg);
  color: var(--ds-text-primary);
  padding: 40px 24px 104px;
}
.biblioteca__container {
  width: 100%;
  max-width: 962px;
  margin: 0 auto;
  box-sizing: border-box;
}
.biblioteca__hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 467px;
  align-items: center;
  gap: 24px;
}
.biblioteca__hero-copy {
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
}
.biblioteca__hero-title {
  font-family: var(--ds-font-family-bebas);
  font-size: 40px;
  line-height: 44px;
  font-weight: 700;
}
.biblioteca__hero-title-line {
  display: flex;
  flex-wrap: wrap;
  gap: 0 8px;
}
.biblioteca__hero-title-accent {
  color: var(--ds-purple-900);
}
.biblioteca__hero-title-base {
  color: var(--ds-purple-400);
}
.biblioteca__hero-subtitle {
  color: var(--ds-purple-900);
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
}
.biblioteca__hero-paragraph {
  font-size: 16px;
  line-height: 24px;
}
.biblioteca__hero-media {
  display: block;
  height: 400px;
  min-width: 0;
}
.biblioteca__hero-image {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 24px;
  object-fit: cover;
}
.biblioteca__search {
  margin: 40px 0 144px;
}
.biblioteca__search-wrap {
  display: flex;
  justify-content: center;
}
.biblioteca__search-box {
  width: min(100%, 680px);
  position: relative;
}
.biblioteca__search-icon {
  position: absolute;
  left: 16px;
  top: 20px;
  pointer-events: none;
}
.biblioteca__search-icon-image {
  display: block;
  width: 24px;
  height: 24px;
}
.biblioteca__search-input {
  width: 100%;
  height: 64px;
  border: 1px solid var(--ds-border-card);
  border-radius: 16px;
  background: white;
  padding: 16px 16px 16px 64px;
  font: inherit;
  color: var(--ds-text-primary);
  box-sizing: border-box;
}
.biblioteca__search-results {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 5;
  background: white;
  border: 1px solid var(--ds-border-card);
  border-radius: 12px;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  box-shadow: var(--ds-shadow-sm);
}
.biblioteca__search-results--above {
  top: auto;
  bottom: calc(100% + 8px);
}
.biblioteca__search-result {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: 0;
  background: white;
  text-align: left;
  cursor: pointer;
  font: inherit;
}
.biblioteca__search-result:hover,
.biblioteca__search-result--active {
  background: var(--ds-purple-50);
}
.biblioteca__search-result-label {
  color: var(--ds-purple-900);
  font-size: 14px;
  font-weight: 600;
}
.biblioteca__search-result-category {
  font-size: 12px;
}
.biblioteca__empty {
  padding: 16px;
  margin: 0;
}
.biblioteca__section-copy {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 48px;
}
.biblioteca__section-title {
  color: var(--ds-purple-900);
  font-size: 40px;
  line-height: 44px;
}
.biblioteca__section-description {
  font-size: 16px;
  line-height: 24px;
}
.biblioteca__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px 24px;
  align-items: start;
}
@media (min-width: 600px) and (max-width: 1024px) {
  .biblioteca {
    padding: 32px;
  }
  .biblioteca__hero-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .biblioteca__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 16px;
  }
  .biblioteca__hero-media {
    height: 364px;
  }
}
@media (max-width: 599px) {
  .biblioteca {
    padding: 32px 16px 64px;
  }
  .biblioteca__hero-grid {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
  .biblioteca__hero-media {
    order: -1;
    width: 100%;
    height: auto;
    aspect-ratio: 328 / 240;
  }
  .biblioteca__hero-copy {
    gap: 24px;
    width: 100%;
  }
  .biblioteca__hero-title {
    font-size: 40px;
    line-height: 44px;
  }
  .biblioteca__search {
    margin: 32px 0 104px;
  }
  .biblioteca__search-input {
    height: 48px;
    padding-left: 48px;
    font-size: 14px;
  }
  .biblioteca__search-icon {
    top: 12px;
  }
  .biblioteca__section-copy {
    margin-bottom: 32px;
  }
  .biblioteca__grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 32px;
  }
}
</style>
